import type { GetCurrentMemberFn } from '@/features/iam'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type {
  ArchiveDocumentFn,
  DocumentCreationModalViewModelFactory,
  JobDocumentListViewModelFactory,
  DocumentPickerViewModelFactory,
  DownloadDocumentsFn,
  SearchDocumentsFn,
  ShowDocumentDetailsFn,
  UploadBarViewModelFactory,
} from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import type {
  DocumentType,
  WellKnownDocumentTypeShortId,
} from '@/features/documents/DocumentType'
import type { UploadDocumentOptions } from '@/features/documents/DocumentStore'
import type { JobDocumentListFilter } from '@/features/documents/JobDocumentListViewModel'
import { Id } from '@/infra/id'
import { type Job } from '@/features/jobs/Job'
import { Member } from '@/features/iam/members/Member'
import { TabController } from '@/infra/TabController'
import { type Checklist } from '@/features/jobs/info-gathering/Checklist'
import { type Observable, tap } from 'rxjs'
import { tapAction, toStream } from '@/utils/mobx-util'
import { displayTitle } from '@/features/jobs/details/display-title'
import { type TxfJobCardViewModel } from '@/features/jobs/details/components/TxfJobCard/TxfJobCardViewModel'
import { type DocumentManualVerificationModalViewModel } from '@/features/jobs/details/components/DocumentManualVerificationModal/DocumentManualVerificationModalViewModel'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { DeleteJobDocumentsDialogViewModel } from '@/features/jobs/details/components/DeleteJobDocumentsDialog/DeleteJobDocumentsDialogViewModel'

const otherDocumentTypeShortId: WellKnownDocumentTypeShortId = 'OTHER'

const allDocumentTypes = ''

/**
 * Documents section tab.
 */
export type DocumentsSectionTab = 'ClientChecklist' | 'Documents'

/**
 * The job's documents: listing, uploading, selecting and acting on them.
 */
export class JobDocumentsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref accessor documentTypes: DocumentType[] = []

  readonly uploadBarVm

  readonly listVm

  @observable
  accessor documentCreationVm: ReturnType<DocumentCreationModalViewModelFactory> | null =
    null

  readonly deleteSelectedDocumentsDialogVm

  /**
   * Narrows the upload menu's document types by name.
   */
  @observable accessor documentTypeQuery = ''

  /**
   * Narrows the documents table by document name and type.
   */
  @observable accessor documentQuery = ''

  /**
   * ID of the document type the documents table is narrowed to, or all types.
   */
  @observable accessor documentTypeFilter = allDocumentTypes

  /**
   * TabController for the Documents section tabs.
   */
  readonly documentsTabController = new TabController<DocumentsSectionTab>(
    () => [
      {
        tab: 'ClientChecklist',
        title: 'Client checklist',
        hasError: () => false,
      },
      {
        tab: 'Documents',
        title: 'Documents',
        hasError: () => false,
      },
    ],
  )

  constructor(
    public readonly job: Job,
    private readonly _isPartOfClientTeam: () => boolean,
    private readonly _isPartOfProTeam: () => boolean,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    public readonly viewDocumentDetails: ShowDocumentDetailsFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getChecklist: () => Checklist | undefined,
    private readonly _manualVerificationRequired$: Observable<Document>,
    public readonly documentManualVerificationVm: DocumentManualVerificationModalViewModel,
    public readonly documentLinkingPickerVm: ReturnType<DocumentPickerViewModelFactory>,
    public readonly txfJobVm: TxfJobCardViewModel,
    createJobDocumentListViewModel: JobDocumentListViewModelFactory,
    private readonly _searchDocuments: SearchDocumentsFn,
    private readonly _documentTypes$: Observable<DocumentType[]>,
    private readonly _archiveDocument: ArchiveDocumentFn,
    private readonly _downloadDocuments: DownloadDocumentsFn,
    private readonly _createUploadBarViewModel: UploadBarViewModelFactory,
    private readonly _createDocumentCreationModalViewModel: DocumentCreationModalViewModelFactory,
  ) {
    this.uploadBarVm = this._createUploadBarViewModel({ autoDismiss: true })

    this.deleteSelectedDocumentsDialogVm =
      new DeleteJobDocumentsDialogViewModel(
        this._createAlertErrorHandler,
        this._archiveDocument,
      )

    this.listVm = createJobDocumentListViewModel({
      jobId: this.job.id,
      workspaceId: Id.assert(this.job.workspace.id),
      uploadBarVm: this.uploadBarVm,
      filter: () => this._filter,
      onView: this.viewDocument,
    })

    this._lifecycle.manage(this.uploadBarVm)
    this._lifecycle.manage(txfJobVm)
    this._lifecycle.manage(this.listVm)
    this._lifecycle.manage(
      this._documentTypes$.pipe(
        tapAction((documentTypes) => {
          this.documentTypes = documentTypes
        }),
      ),
    )
    this._lifecycle.manage(
      this._manualVerificationRequired$.pipe(
        tap((document) => {
          if (this.documents.includes(document)) {
            this.documentManualVerificationVm.open(document, this.job)
          }
        }),
      ),
    )
    this._lifecycle.manage(
      // The checklist is the landing tab whenever the job has one.
      toStream(() => this.checklist, true).pipe(
        tap(
          action(() => {
            this.documentsTabController.change(
              this.hasSupportingItems ? 'ClientChecklist' : 'Documents',
            )
          }),
        ),
      ),
    )
  }

  @computed
  get checklist() {
    return this._getChecklist()
  }

  @computed
  get supportingItems() {
    return this.checklist?.sections ?? []
  }

  @computed
  get hasSupportingItems() {
    return this.supportingItems.length > 0
  }

  /**
   * Get user's collaborator type on the current job.
   */
  @computed
  get collaboratorType() {
    const userPublicId = this._getCurrentMember().user.publicId
    const collaborator = this.job.collaborators.find(
      (collaborator) => collaborator.member.user.publicId === userPublicId,
    )

    return (
      collaborator?.type ||
      (this._isPartOfClientTeam() && 'Client') ||
      (this._isPartOfProTeam() && 'Pro')
    )
  }

  /**
   * The loaded documents linked to the job, newest first.
   */
  @computed
  get documents() {
    return this.listVm.documents
  }

  /**
   * Whether we're fetching the initial documents.
   */
  @computed
  get isFetchingInitial() {
    return (
      this.scrollProvider.fetch.pending &&
      !this.scrollProvider.isRevalidating &&
      // The store keeps the job's documents from an earlier visit, and those
      // stay up while the activation fetch resets the scroller.
      this.documents.length === 0
    )
  }

  @computed
  get scrollProvider() {
    return this.listVm.scrollProvider
  }

  @computed
  get selectedDocuments() {
    return this.listVm.selection
  }

  /**
   * How many documents match the filters. The first page reports the total,
   * so it can be shown before the remaining pages are scrolled in.
   */
  @computed
  get documentCount() {
    return Math.max(this.shownDocuments.length, this.scrollProvider.total)
  }

  /**
   * Get document types available to the current user.
   * Users with the ADMIN_DOCUMENT_MANAGE permission see all types,
   * otherwise types are filtered by the user's collaborator role.
   */
  @computed
  get documentTypesForUser() {
    if (this.jobWorkspaceMember.permissions.adminDocumentManage) {
      return this.documentTypes
    }

    const mappedCollaboratorType = () => {
      switch (this.collaboratorType) {
        case 'Client':
          return 'CLIENT'
        case 'Pro':
          return 'PRO'
        default:
          return undefined
      }
    }

    const collaborator = mappedCollaboratorType()
    return this.documentTypes.filter(
      (type) =>
        type.id === otherDocumentTypeShortId ||
        type.uploaderUserType.toUpperCase() === collaborator,
    )
  }

  /**
   * The catch-all type, offered separately from the named types.
   */
  @computed
  get otherDocumentType() {
    return this.documentTypesForUser.find(
      (t) => t.id === otherDocumentTypeShortId,
    )
  }

  /**
   * Named document types the user can upload, alphabetically, narrowed by
   * {@link documentTypeQuery}.
   */
  @computed
  get uploadableDocumentTypes() {
    const query = this.documentTypeQuery.toLowerCase().trim()
    return this.documentTypesForUser
      .filter((t) => t.id !== otherDocumentTypeShortId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((t) => !query || t.name.toLowerCase().includes(query))
  }

  /**
   * The document types the table can be narrowed to, alphabetically.
   */
  @computed
  get filterableDocumentTypes() {
    return this.documentTypes
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * The documents the table shows after its filters. The server applies the
   * same filters when paging, so this only narrows what was loaded before
   * the filters changed.
   */
  @computed
  get shownDocuments() {
    return this.documents.filter((d) => this._isShown(d))
  }

  /**
   * View models for the {@link shownDocuments}.
   */
  @computed
  get shownDocumentVms() {
    return this.listVm.documentVms.filter((vm) => this._isShown(vm.document))
  }

  @computed
  get areAllShownDocumentsSelected() {
    return (
      this.shownDocuments.length > 0 &&
      this.shownDocuments.every((d) => this.selectedDocuments.has(d))
    )
  }

  /**
   * The workspace member for the current user on the workspace
   * that the job belongs to.
   */
  @computed
  get jobWorkspaceMember() {
    return this._getCurrentMember(this.job.workspace.id) ?? Member.none
  }

  /**
   * Whether the user can remove documents
   */
  @computed
  get canRemoveDocs() {
    const { permissions } = this.jobWorkspaceMember
    return (
      permissions.docsSelfRemove ||
      permissions.docsClientRemove ||
      permissions.docsProviderRemove
    )
  }

  @computed
  get isJobClosed() {
    return this.job.status === 'Closed'
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The document list renders a failure from the task's state.
    void this.listVm.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.documentLinkingPickerVm.modal.close()
  }

  @action.bound
  stageFileAsType(files: FileList, type: DocumentType) {
    return this.newDocument(files, type)
  }

  @action.bound
  viewDocument(document: Document) {
    if (document.isManualAttestationRequired && !document.archived) {
      this.documentManualVerificationVm.open(document, this.job)
      return
    }

    const viewAsAdmin = this.jobWorkspaceMember.permissions.adminDocumentManage
    void this.viewDocumentDetails(document, viewAsAdmin)
  }

  @action.bound
  async newDocument(files: FileList, type: DocumentType | undefined) {
    if (files.length < 1) {
      return false
    }

    const filesArray = Array.from(files)

    const filesToUpload = filesArray.map((f) =>
      this._makeUploadRequest(f, type),
    )

    try {
      await this.uploadBarVm
        .uploadFiles(filesToUpload)
        .catch(this._createAlertErrorHandler('Uploading the documents failed'))
        .finally(() => {
          this.documentCreationVm = null
        })
      return true
    } catch {
      return false
    }
  }

  @action.bound
  setSelected(document: Document, selected: boolean) {
    if (selected) {
      this.selectedDocuments.add(document)
    } else {
      this.selectedDocuments.delete(document)
    }
  }

  @action.bound
  clearSelection() {
    this.selectedDocuments.clear()
  }

  @action.bound
  toggleAllShownDocuments() {
    const selected = !this.areAllShownDocumentsSelected
    this.shownDocuments.forEach((d) => this.setSelected(d, selected))
  }

  @action.bound
  setDocumentTypeQuery(query: string) {
    this.documentTypeQuery = query
  }

  @action.bound
  setDocumentQuery(query: string) {
    this.documentQuery = query
  }

  @action.bound
  setDocumentTypeFilter(typeId: string) {
    this.documentTypeFilter = typeId
  }

  typeName(document: Document) {
    return document.documentType?.name ?? 'Other'
  }

  downloadAll = task.resolved(async () => {
    const processingAlert = this._newAlert()
      .saying(
        'Processing',
        'Your file is being created in the background and will download once ready. Please do not close this browser window.',
      )
      .withState('pending')
      .show()

    try {
      const ids = (await this._fetchAllDocuments())
        .filter((d) => d.downloadable)
        .map((d) => d.id)
      await this._downloadDocuments(ids)
    } catch (err) {
      this._createAlertErrorHandler('Downloading the documents failed')(err)
    } finally {
      processingAlert.dismiss()
    }
  })

  downloadSelected = task.resolved(async () => {
    const processingAlert = this._newAlert()
      .saying(
        'Processing',
        'Your file is being created in the background and will download once ready. Please do not close this browser window.',
      )
      .withState('pending')
      .show()

    const ids = Array.from(this.selectedDocuments.values()).map(
      (document) => document.id,
    )

    try {
      await this._downloadDocuments(ids)
    } catch (err) {
      this._createAlertErrorHandler('Downloading the documents failed')(err)
    } finally {
      processingAlert.dismiss()
    }
  })

  @action.bound
  async deleteSelected() {
    const documents = Array.from(this.selectedDocuments.values())
    if (
      !(await this.deleteSelectedDocumentsDialogVm.prompt(
        documents,
        this._lifecycle.signal,
      ))
    ) {
      return
    }

    this.clearSelection()
  }

  /**
   * Opens the document creation dialog for files dropped on the job.
   *
   * @param files
   */
  @action.bound
  dropFiles(files: FileList) {
    this.viewDocumentCreation(Array.from(files))
  }

  /**
   * Opens the document creation dialog for the files.
   *
   * @param files
   */
  @action.bound
  viewDocumentCreation(files: File[]) {
    this.documentCreationVm = this.createDocumentCreationModalViewModel(files)
    this.documentCreationVm.modal.show()
  }

  createDocumentCreationModalViewModel(files: File[]) {
    return this._createDocumentCreationModalViewModel(
      files,
      () => this.documentTypesForUser,
      this.job,
      this.uploadBarVm,
    )
  }

  @computed
  private get _filter(): JobDocumentListFilter {
    return {
      searchText: this.documentQuery.trim() || undefined,
      documentTypeId: this.documentTypeFilter || undefined,
    }
  }

  private _isShown(document: Document) {
    const { searchText, documentTypeId } = this._filter
    const query = searchText?.toLowerCase()
    return (
      (!documentTypeId ||
        (document.documentType?.id ?? otherDocumentTypeShortId) ===
          documentTypeId) &&
      (!query || displayTitle(document.title).toLowerCase().includes(query))
    )
  }

  /**
   * Pages through every document on the job, regardless of the table's
   * filters.
   */
  private async _fetchAllDocuments() {
    const documents: Document[] = []
    let after: string | undefined
    do {
      const page = await this._searchDocuments({
        context: 'Job',
        jobId: this.job.id,
        limit: 100,
        after,
      })
      documents.push(...page.data)
      after = page.next ?? undefined
    } while (after)
    return documents
  }

  private _makeUploadRequest(
    file: File,
    type: DocumentType | undefined,
  ): UploadDocumentOptions {
    return {
      file,
      workspaceId: Id.assert(this.job.workspace.id),
      jobId: this.job.id,
      documentTypeId: type?.id,
    }
  }
}
